import React, { useState, useEffect } from 'react';
import { Trash2, History, Coins, X } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { supabase } from '../../../config/supabase';
import { useAuth } from '../../../context/AuthContext';
import { formatDateFR } from '../../../utils/date';
import Modal from '../../shared/components/Modal';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export default function ClientHistoryModal({ client, onClose }) {
  const { lang, t } = useLanguage();
  const { agencyId } = useAuth();
  const isAr = lang === 'ar';

  const [payments, setPayments] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payments');
  
  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Espèces');
  const [payNote, setPayNote] = useState('');
  const [currentBalance, setCurrentBalance] = useState(client.creditBalance || 0);
  const [saving, setSaving] = useState(false);

  const _sbRowToDoc = (row) => {
    if (!row) return null;
    const fields = { ...(row.data || {}), _id: row.id };
    if (row.agency_id) fields.agency_id = row.agency_id;
    return { id: row.id, ...fields };
  };

  const loadHistory = async () => {
    if (!agencyId || !client.id) return;
    setLoading(true);
    try {
      // Fetch payments where data->>clientId == client.id
      const { data: payRows, error: payErr } = await supabase
        .from('client_payments')
        .select('*')
        .eq('agency_id', agencyId)
        .eq('data->>clientId', client.id);

      if (payErr) throw payErr;
      const pays = (payRows || []).map(_sbRowToDoc);
      pays.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      setPayments(pays);
      
      // Fetch rentals where data->>clientId == client.id
      const { data: rentRows, error: rentErr } = await supabase
        .from('rentals')
        .select('*')
        .eq('agency_id', agencyId)
        .eq('data->>clientId', client.id);

      if (rentErr) throw rentErr;
      const rents = (rentRows || []).map(_sbRowToDoc);
      rents.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
      setRentals(rents);
    } catch (err) {
      console.error('Load history error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [client.id, agencyId]);

  const submitInlinePayment = async () => {
    const val = parseFloat(payAmount);
    if (!val || val <= 0) {
      return alert(isAr ? '⚠️ يرجى إدخال مبلغ صالح' : '⚠️ Montant invalide');
    }
    if (val > currentBalance) {
      const confirmOverpay = window.confirm(
        isAr 
          ? `⚠️ المبلغ ${val} أكبر من الدين الحالي ${currentBalance}. الزيادة ستعتبر كرصيد مسبق (avance). هل تريد المتابعة؟`
          : `⚠️ Le montant ${val} dépasse le crédit ${currentBalance}. Le surplus sera comptabilisé comme avance. Continuer?`
      );
      if (!confirmOverpay) return;
    }
    
    setSaving(true);
    try {
      const newBalance = currentBalance - val;
      const timestamp = new Date().toISOString();
      const paymentDate = timestamp.split('T')[0];
      
      // 1. Update client balance
      const { data: existingClient } = await supabase
        .from('clients')
        .select('data')
        .eq('id', client.id)
        .single();
        
      const mergedClient = { 
        ...(existingClient?.data || {}), 
        creditBalance: newBalance,
        lastPaymentDate: paymentDate 
      };
      
      const { error: clientErr } = await supabase
        .from('clients')
        .update({ data: mergedClient })
        .eq('id', client.id);
        
      if (clientErr) throw clientErr;
      
      // 2. Save payment to history
      const paymentPayload = {
        clientId: client.id,
        clientName: client.name,
        amount: val,
        previousBalance: currentBalance,
        newBalance: newBalance,
        paymentMethod: payMethod,
        note: payNote || '',
        date: paymentDate,
        timestamp: timestamp,
        createdBy: 'manual',
        isAdvance: newBalance < 0
      };
      
      const { error: payErr } = await supabase
        .from('client_payments')
        .insert({
          agency_id: agencyId,
          created_at: timestamp,
          data: paymentPayload
        });
        
      if (payErr) throw payErr;

      // Telegram notification
      try {
        const saved = localStorage.getItem('appSettings');
        let currency = 'MAD';
        if (saved) {
          try {
            const settings = JSON.parse(saved);
            currency = settings.currency || 'MAD';
          } catch (_) {}
        }
        await sendTelegramNotification(
          buildTelegramMessage('client_history_payment', {
            clientName: client.name, amount: val, currency,
            method: payMethod, note: payNote, balance: newBalance
          }),
          'client_history_payment'
        );
      } catch (tgErr) {
        console.error('Failed to send payment Telegram notification:', tgErr);
      }
      
      // 3. Update local state
      setCurrentBalance(newBalance);
      
      // 4. Reload history
      await loadHistory();
      
      // 5. Reset form
      setPayAmount('');
      setPayNote('');
      setShowPayForm(false);
      
      const msg = newBalance < 0
        ? (isAr 
            ? `✅ تم تسجيل ${val} درهم!\nرصيد مسبق (avance): ${Math.abs(newBalance)} درهم`
            : `✅ Paiement de ${val} MAD enregistré!\nAvance restante: ${Math.abs(newBalance)} MAD`)
        : (isAr 
            ? `✅ تم تسجيل ${val} درهم!\nالرصيد المتبقي: ${newBalance} درهم`
            : `✅ Paiement de ${val} MAD enregistré!\nSolde restant: ${newBalance} MAD`);
      alert(msg);
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(
      isAr 
        ? 'حذف هذا الدفع؟ سيتم إرجاع المبلغ إلى دين العميل.'
        : 'Supprimer ce paiement? Le montant sera réajouté au crédit du client.'
    )) return;

    try {
      // Reverse the client balance
      const newBalance = currentBalance + parseFloat(p.amount);
      
      const { data: existingClient } = await supabase
        .from('clients')
        .select('data')
        .eq('id', client.id)
        .single();
        
      const mergedClient = { 
        ...(existingClient?.data || {}), 
        creditBalance: newBalance 
      };
      
      const { error: clientErr } = await supabase
        .from('clients')
        .update({ data: mergedClient })
        .eq('id', client.id);
        
      if (clientErr) throw clientErr;

      const { error: delErr } = await supabase
        .from('client_payments')
        .delete()
        .eq('id', p.id);

      if (delErr) throw delErr;

      setCurrentBalance(newBalance);
      setPayments(prev => prev.filter(x => x.id !== p.id));
      alert(isAr ? '✅ تم حذف الدفع' : '✅ Paiement supprimé');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalRented = rentals.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0);
  const activeRentalsCount = rentals.filter(r => r.status === 'active').length;

  return (
    <Modal onClose={onClose} title={`📋 ${isAr ? 'سجل العميل' : 'Historique du client'}`}>
      <div>
        {/* Client header */}
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">👤</span>
                <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
              </div>
              {client.phone && <p className="text-xs text-gray-600 font-mono">📞 {client.phone}</p>}
              {client.cin && <p className="text-xs text-gray-600 font-mono">🆔 {client.cin}</p>}
            </div>
            
            <div className="text-right flex flex-col items-end gap-2">
              <div>
                <p className="text-xs text-gray-600 font-bold uppercase">
                  {currentBalance < 0 
                    ? (isAr ? 'رصيد مسبق' : 'Avance')
                    : (isAr ? 'الدين الحالي' : 'Crédit actuel')}
                </p>
                <p className={`text-2xl font-black ${
                  currentBalance > 0 ? 'text-red-600' : 
                  currentBalance < 0 ? 'text-blue-600' : 'text-green-600'
                }`}>
                  {Math.abs(currentBalance)} MAD
                  {currentBalance < 0 && <span className="text-sm ms-1">✨</span>}
                </p>
              </div>
              
              {!showPayForm && (
                <button
                  type="button"
                  onClick={() => {
                    setShowPayForm(true);
                    setPayAmount('');
                    setPayNote('');
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-sm shadow-md transition"
                >
                  💰 {isAr ? 'دفع' : 'Payer'}
                </button>
              )}
            </div>
          </div>
          
          {/* INLINE PAY FORM */}
          {showPayForm && (
            <div className="mt-4 pt-4 border-t-2 border-blue-300">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-green-900 flex items-center gap-2">
                  💰 {isAr ? 'تسجيل دفعة جديدة' : 'Nouveau paiement'}
                </h4>
                <button
                  onClick={() => { setShowPayForm(false); setPayAmount(''); setPayNote(''); }}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    💵 {isAr ? 'المبلغ (MAD)' : 'Montant (MAD)'}
                  </label>
                  <input
                    type="number"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    placeholder={isAr ? 'كتاب المبلغ هنا' : 'Tapez le montant'}
                    step="0.01"
                    className="w-full px-3 py-2 text-lg font-bold border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    💳 {isAr ? 'طريقة الدفع' : 'Mode'}
                  </label>
                  <select
                    value={payMethod}
                    onChange={e => setPayMethod(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="Espèces">{isAr ? '💵 نقداً' : '💵 Espèces'}</option>
                    <option value="Chèque">{isAr ? '📄 شيك' : '📄 Chèque'}</option>
                    <option value="Virement">{isAr ? '🏦 تحويل' : '🏦 Virement'}</option>
                    <option value="Carte">{isAr ? '💳 بطاقة' : '💳 Carte'}</option>
                    <option value="Autre">{isAr ? '❓ أخرى' : '❓ Autre'}</option>
                  </select>
                </div>
              </div>
              
              <input
                type="text"
                value={payNote}
                onChange={e => setPayNote(e.target.value)}
                placeholder={isAr ? '📝 ملاحظة (اختياري)' : '📝 Note (optionnel)'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 mb-3"
              />
              
              {payAmount && parseFloat(payAmount) > 0 && (
                <div className={`mb-2 p-2 rounded-lg text-xs ${
                  parseFloat(payAmount) > currentBalance
                    ? 'bg-blue-50 border border-blue-200 text-blue-800'
                    : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  {parseFloat(payAmount) > currentBalance ? (
                    <div>
                      <span className="font-bold">{isAr ? '✨ سيشمل رصيد مسبق: ' : '✨ Crée une avance de: '}</span>
                      <strong className="font-mono">{(parseFloat(payAmount) - currentBalance).toFixed(2)} MAD</strong>
                    </div>
                  ) : (
                    <div>
                      <span className="font-bold">{isAr ? '📊 الرصيد بعد: ' : '📊 Solde après: '}</span>
                      <strong className="font-mono">{(currentBalance - parseFloat(payAmount)).toFixed(2)} MAD</strong>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowPayForm(false); setPayAmount(''); setPayNote(''); }}
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-sm"
                >
                  {isAr ? 'إلغاء' : 'Annuler'}
                </button>
                <button
                  onClick={submitInlinePayment}
                  disabled={saving || !payAmount || parseFloat(payAmount) <= 0}
                  className={`flex-1 px-3 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-1.5 transition ${
                    saving || !payAmount || parseFloat(payAmount) <= 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white shadow-md'
                  }`}
                >
                  {saving ? (
                    <span>⏳ {isAr ? 'جاري...' : 'Enregistrement...'}</span>
                  ) : (
                    <span>✅ {isAr ? 'تأكيد' : 'Valider'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <div className="text-xs text-blue-700 font-bold uppercase">{isAr ? 'إيجارات' : 'Locations'}</div>
            <div className="text-xl font-black text-blue-900 mt-1">{rentals.length}</div>
            {activeRentalsCount > 0 && (
              <div className="text-[10px] text-amber-700 mt-0.5">
                {activeRentalsCount} {isAr ? 'نشطة' : 'active'}
              </div>
            )}
          </div>
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
            <div className="text-xs text-purple-700 font-bold uppercase">{isAr ? 'إجمالي المعاملات' : 'Total facturé'}</div>
            <div className="text-base font-black text-purple-900 mt-1 font-mono">{totalRented.toFixed(0)} MAD</div>
          </div>
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
            <div className="text-xs text-green-700 font-bold uppercase">{isAr ? 'إجمالي المدفوع' : 'Total payé'}</div>
            <div className="text-base font-black text-green-900 mt-1 font-mono">{totalPaid.toFixed(0)} MAD</div>
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
            <div className="text-xs text-amber-700 font-bold uppercase">{isAr ? 'عدد المدفوعات' : 'Nb paiements'}</div>
            <div className="text-xl font-black text-amber-900 mt-1">{payments.length}</div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-3">
          <button
            onClick={() => setActiveTab('payments')}
            className={`flex-1 px-4 py-2 font-bold text-sm border-b-2 transition ${
              activeTab === 'payments' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            💰 {isAr ? 'المدفوعات' : 'Paiements'} ({payments.length})
          </button>
          <button
            onClick={() => setActiveTab('rentals')}
            className={`flex-1 px-4 py-2 font-bold text-sm border-b-2 transition ${
              activeTab === 'rentals' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🚗 {isAr ? 'الإيجارات' : 'Locations'} ({rentals.length})
          </button>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="mt-2 text-sm text-gray-600">{isAr ? 'جاري التحميل...' : 'Chargement...'}</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {activeTab === 'payments' && (
              payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">💰</div>
                  <p className="text-sm">{isAr ? 'لا توجد مدفوعات بعد' : 'Aucun paiement enregistré'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {payments.map(p => (
                    <div 
                      key={p.id} 
                      className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between gap-3 hover:bg-green-100 transition"
                    >
                      <div className="flex-1 min-w-0 font-sans">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">✅</span>
                          <span className="font-bold text-green-900 text-sm">+{p.amount} MAD</span>
                          <span className="text-[10px] bg-white border border-green-300 px-1.5 py-0.5 rounded text-green-700 font-bold">
                            {p.paymentMethod || 'Espèces'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 font-mono">
                          📅 {p.timestamp ? new Date(p.timestamp).toLocaleString(isAr ? 'ar-MA' : 'fr-FR') : p.date}
                        </div>
                        {p.note && <div className="text-xs text-gray-700 mt-1 italic truncate">📝 {p.note}</div>}
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {isAr ? 'قبل' : 'Avant'}: {p.previousBalance} → {isAr ? 'بعد' : 'Après'}: {p.newBalance} MAD
                        </div>
                      </div>
                      <button
                        onClick={() => deletePayment(p)}
                        className="p-1.5 text-red-500 hover:bg-red-100 rounded transition"
                        title={isAr ? 'حذف' : 'Supprimer'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
            
            {activeTab === 'rentals' && (
              rentals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🚗</div>
                  <p className="text-sm">{isAr ? 'لا توجد إيجارات بعد' : 'Aucune location enregistrée'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {rentals.map(r => (
                    <div 
                      key={r.id} 
                      className={`p-3 border rounded-lg hover:bg-gray-50 transition ${
                        r.status === 'active' ? 'bg-amber-50 border-amber-200' :
                        r.status === 'completed' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🚗</span>
                          <span className="font-bold text-sm text-gray-900">{r.carName || 'Voiture'}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                            r.status === 'active' ? 'bg-amber-200 text-amber-900' :
                            r.status === 'completed' ? 'bg-green-200 text-green-900' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {r.status === 'active' ? '🟡 En cours' : r.status === 'completed' ? '✅ Terminé' : r.status}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-blue-700 font-mono">{r.totalAmount || 0} MAD</span>
                      </div>
                      <div className="text-xs text-gray-600 font-mono">
                        📅 {formatDateFR(r.startDate) || '?'}
                        {r.endDate ? ' → ' + formatDateFR(r.endDate) : (r.isOpenContract ? ' (ouvert ⏳)' : '')}
                      </div>
                      {r.days && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {r.days} {isAr ? 'يوم' : 'jours'} × {r.dailyRate || 0} MAD
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
        
        <div className="mt-4 pt-3 border-t border-gray-200 text-end">
          <button 
            onClick={onClose} 
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-sm" 
          >
            {isAr ? 'إغلاق' : 'Fermer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
